/**
 * Left rail — ordered list of slides with the section header and a
 * stripped title preview. Click selects.
 *
 * PR3 adds:
 *   - An "Add slide" button in the rail header that opens the layout
 *     picker via ``onAdd()``.
 *   - HTML5 drag-and-drop reordering (no external library — the rail
 *     has a tiny fixed set of items, so the native API is enough and
 *     ships zero extra bytes). The component holds an in-flight drag
 *     state ref locally; the parent only learns the final ordering via
 *     ``onReorder(newOrder)`` once a drop lands.
 *
 * Drop semantics: we treat the drag-over position as "drop above this
 * item" — so a drop on item 2 with a source at item 0 moves it to
 * position 1 (because 0 is removed first, then re-inserted before 2's
 * pre-drop position, which has now shifted to index 1).
 */

import { useRef, useState } from "react";

import type { Slide } from "../types";

interface SlideRailProps {
  slides: Slide[];
  activeIdx: number;
  onSelect(idx: number): void;
  /**
   * Optional. When provided, an "Add slide" button appears in the rail
   * header. PR1/PR2 callers don't pass this — pass it from the editor
   * shell to enable PR3 slide management.
   */
  onAdd?(): void;
  /**
   * Optional. When provided, slides become draggable; the parent gets
   * the new ordering as a permutation of original indices on drop.
   * ``newOrder[i]`` is the original idx of the slide that should now
   * appear at position ``i``.
   */
  onReorder?(newOrder: number[]): void;
  /**
   * Disable interactive add/reorder while a previous request is still
   * in flight. The rail itself stays clickable (selecting other slides
   * is fine), but Add and DnD are inert.
   */
  disabled?: boolean;
}

export function SlideRail({
  slides,
  activeIdx,
  onSelect,
  onAdd,
  onReorder,
  disabled = false,
}: SlideRailProps) {
  const [dragSrcIdx, setDragSrcIdx] = useState<number | null>(null);
  const [dragOverIdx, setDragOverIdx] = useState<number | null>(null);
  // We hold the live drag state in a ref so the drop handler doesn't
  // close over a stale ``dragSrcIdx`` from a render that hadn't yet
  // committed.
  const dragSrcRef = useRef<number | null>(null);

  const draggable = Boolean(onReorder) && !disabled;

  function startDrag(idx: number, e: React.DragEvent<HTMLLIElement>) {
    if (!draggable) return;
    dragSrcRef.current = idx;
    setDragSrcIdx(idx);
    e.dataTransfer.effectAllowed = "move";
    // Setting some data is required for Firefox to actually fire drop
    // events. The payload itself is ignored — we use the ref.
    try {
      e.dataTransfer.setData("text/plain", String(idx));
    } catch {
      // Some browser test harnesses don't implement DataTransfer; the
      // ref is the source of truth so this is non-fatal.
    }
  }

  function handleDragOver(idx: number, e: React.DragEvent<HTMLLIElement>) {
    if (!draggable) return;
    if (dragSrcRef.current === null) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    if (dragOverIdx !== idx) setDragOverIdx(idx);
  }

  function handleDrop(idx: number, e: React.DragEvent<HTMLLIElement>) {
    if (!draggable) return;
    e.preventDefault();
    const src = dragSrcRef.current;
    dragSrcRef.current = null;
    setDragSrcIdx(null);
    setDragOverIdx(null);
    if (src === null || src === idx) return;
    const order = computeReorder(slides.length, src, idx);
    onReorder?.(order);
  }

  function handleDragEnd() {
    dragSrcRef.current = null;
    setDragSrcIdx(null);
    setDragOverIdx(null);
  }

  return (
    <aside style={railStyle} data-testid="slide-rail">
      <header style={railHeaderStyle}>
        <h2 style={{ margin: 0, fontSize: "0.85em", color: "var(--gray-11)" }}>
          Slides
        </h2>
        {onAdd ? (
          <button
            type="button"
            onClick={onAdd}
            disabled={disabled}
            style={{
              ...addButtonStyle,
              opacity: disabled ? 0.55 : 1,
              cursor: disabled ? "not-allowed" : "pointer",
            }}
            data-testid="slide-rail-add-button"
          >
            + Add
          </button>
        ) : null}
      </header>
      <ol style={{ listStyle: "none", margin: 0, padding: 0 }}>
        {slides.map((s, idx) => {
          const isActive = idx === activeIdx;
          const isDragSource = dragSrcIdx === idx;
          const isDropTarget = dragOverIdx === idx && dragSrcIdx !== idx;
          return (
            <li
              key={`${s.slug}-${idx}`}
              draggable={draggable}
              onDragStart={(e) => startDrag(idx, e)}
              onDragOver={(e) => handleDragOver(idx, e)}
              onDragLeave={() => {
                if (dragOverIdx === idx) setDragOverIdx(null);
              }}
              onDrop={(e) => handleDrop(idx, e)}
              onDragEnd={handleDragEnd}
              data-testid={`slide-rail-row-${idx}`}
              data-drop-target={isDropTarget || undefined}
              data-drag-source={isDragSource || undefined}
              style={{
                opacity: isDragSource ? 0.4 : 1,
                borderTop: isDropTarget
                  ? "2px solid var(--accent-9)"
                  : "2px solid transparent",
              }}
            >
              <button
                type="button"
                onClick={() => onSelect(idx)}
                style={{
                  ...rowStyle,
                  background: isActive ? "var(--accent-3)" : "transparent",
                  borderLeft: isActive
                    ? "3px solid var(--accent-9)"
                    : "3px solid transparent",
                  cursor: draggable ? "grab" : "pointer",
                }}
                data-testid={`slide-rail-item-${idx}`}
                data-active={isActive || undefined}
              >
                <span style={numStyle}>
                  {String(idx + 1).padStart(2, "0")}
                </span>
                <span style={{ flex: 1 }}>
                  <span style={sectionStyle}>{s.section || "—"}</span>
                  <span style={titleStyle}>{stripHtml(s.titleHtml)}</span>
                </span>
              </button>
            </li>
          );
        })}
      </ol>
    </aside>
  );
}

function stripHtml(html: string): string {
  // Cheap server-side-safe strip; this is for display only.
  return html
    .replace(/<[^>]*>/g, "")
    .replace(/&[a-zA-Z]+;/g, " ")
    .trim();
}

/**
 * Build the new ordering permutation when slide ``src`` is dropped onto
 * position ``target``. Exported (and pure) so the test suite can pin
 * the semantics without simulating a DataTransfer.
 *
 * The semantics: drop on item N means "this slide should now appear
 * **above** item N (i.e. at position N if dragging from below, or at
 * position N-1 if dragging from above — i.e. it becomes item N's new
 * position relative to where item N ended up)". In practice we treat
 * the target idx as the insertion-point after the source is removed.
 */
export function computeReorder(
  length: number,
  src: number,
  target: number,
): number[] {
  if (src === target) return Array.from({ length }, (_, i) => i);
  const indices = Array.from({ length }, (_, i) => i);
  const [removed] = indices.splice(src, 1);
  // After removing src, all positions >= src have shifted down by one.
  // The target idx referred to the pre-drop array, so adjust.
  const insertion = src < target ? target - 1 : target;
  // ``removed`` can only be undefined under noUncheckedIndexedAccess if
  // ``src`` is out of range — that's a programming error, but keep the
  // function total by no-op-ing rather than throwing.
  if (removed === undefined) return Array.from({ length }, (_, i) => i);
  indices.splice(insertion, 0, removed);
  return indices;
}

const railStyle: React.CSSProperties = {
  width: 260,
  flex: "0 0 260px",
  borderRight: "1px solid var(--gray-5)",
  background: "var(--gray-1)",
  overflowY: "auto",
};

const railHeaderStyle: React.CSSProperties = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  padding: "0.75rem 1rem",
  borderBottom: "1px solid var(--gray-5)",
  position: "sticky",
  top: 0,
  background: "var(--gray-1)",
  zIndex: 1,
};

const addButtonStyle: React.CSSProperties = {
  fontSize: "0.75em",
  fontWeight: 600,
  padding: "0.3em 0.7em",
  background: "var(--accent-3)",
  color: "var(--accent-11)",
  border: "1px solid var(--accent-7)",
  borderRadius: 4,
};

const rowStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "flex-start",
  gap: "0.75rem",
  padding: "0.6rem 0.75rem",
  border: "none",
  width: "100%",
  textAlign: "left",
  color: "var(--gray-12)",
};

const numStyle: React.CSSProperties = {
  fontVariantNumeric: "tabular-nums",
  color: "var(--gray-9)",
  fontSize: "0.85em",
  fontWeight: 500,
  flex: "0 0 1.5em",
};

const sectionStyle: React.CSSProperties = {
  display: "block",
  fontSize: "0.7em",
  color: "var(--gray-9)",
  textTransform: "uppercase",
  letterSpacing: "0.04em",
  marginBottom: "0.2em",
};

const titleStyle: React.CSSProperties = {
  display: "block",
  fontSize: "0.9em",
  lineHeight: 1.3,
  fontWeight: 500,
};
